/**
 * 🔐 VERIFICATION SERVICE - Complete verification API integration
 * Handles driver verification, document upload, status tracking, and admin operations
 */

import { VerificationStatus } from '@/types';
import { verificationPersistence } from '@/utils/verificationPersistence';
import { getApiBaseUrl } from '@/config/environment';
import { logger } from '@/utils/logger';

const log = logger.module('VerificationService');


// API Base URL

export interface VerificationData {
  userId: string;
  purpose: 'customer_verification' | 'driver_application';
  isDraft: boolean;
  
  // Personal Information
  personalInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    nin: string;
    bvn: string;
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
  };

  // Driver License
  driverLicense: {
    licenseNumber: string;
    licenseClass: string;
    issueDate: string;
    expiryDate: string;
    issuingState: string;
    frontImageUrl?: string;
    backImageUrl?: string;
  };

  // Vehicle Information
  vehicleInfo: {
    hasVehicle: boolean;
    make?: string;
    model?: string;
    year?: string;
    plateNumber?: string;
    insurancePolicyNumber?: string;
    insuranceExpiry?: string;
    registrationDocumentUrl?: string;
    insuranceDocumentUrl?: string;
    vehiclePhotoUrls?: string[];
  };

  // Guarantors
  guarantors: {
    primary: {
      name: string;
      relationship: string;
      phone: string;
      email: string;
      address: string;
    };
    secondary: {
      name: string;
      relationship: string;
      phone: string;
      email: string;
      address: string;
    };
  };

  // Background Check
  backgroundCheck: {
    consentGiven: boolean;
    criminalHistoryDisclosure: string;
    drivingHistoryConsent: boolean;
  };

  // Banking Information
  bankingInfo: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    bankCode: string;
  };
}

export interface VerificationRecord {
  id: string;
  userId: string;
  status: VerificationStatus;
  purpose: string;
  data: VerificationData;
  completionPercentage: number;
  missingFields: string[];
  submittedAt?: string;
  reviewedAt?: string;
  reviewerId?: string;
  adminNotes?: string;
  rejectionReason?: string;
  rejectedFields?: string[]; // Track which specific fields were rejected
  rejectionDetails?: { // Detailed rejection info per field
    [field: string]: {
      reason: string;
      previousValue?: any;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface VerificationProgress {
  completionPercentage: number;
  missingFields: string[];
  completedSections: string[];
  canSubmit: boolean;
}

export interface VerificationListResponse {
  verifications: VerificationRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// New interfaces for admin verification system based on actual API
export interface AdminVerificationRequest {
  id: string;
  verificationId: string;
  userId: string;
  status: 'pending_review' | 'pending_manual_review' | 'verified' | 'rejected';
  documentType: string;
  countryCode: string;
  documentImageUrl: string; // base64 data URI
  selfieImageUrl: string; // base64 data URI
  userInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
  verificationResults?: {
    requiresManualReview?: boolean;
    livenessAttempts?: number;
    livenessScore?: number;
    facialMatchScore?: number;
  };
}

export interface AdminVerificationListResponse {
  success: boolean;
  data: AdminVerificationRequest[];
  total: number;
}

export interface DriverVerificationRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  submittedAt: string;
  status: 'pending_review' | 'under_review' | 'approved' | 'rejected' | 'cancelled';
  completionPercentage: number;
  documentsCount: number;
  reviewedAt?: string;
  reviewerId?: string;
  hasRejectionReason: boolean;
}

export interface DriverVerificationListResponse {
  success: boolean;
  data: {
    verifications: DriverVerificationRequest[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    statistics: {
      total: number;
      byStatus: {
        pending_review: number;
        under_review: number;
        approved: number;
        rejected: number;
        cancelled: number;
      };
    };
  };
}

export const verificationService = {
  
  /**
   * 📝 Create or update verification draft
   */
  async saveVerification(data: VerificationData, progress: VerificationProgress): Promise<{
    success: boolean;
    verification?: VerificationRecord;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/driver-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...data,
          completionPercentage: progress.completionPercentage,
          missingFields: progress.missingFields
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        verification: result.data
      };
    } catch (error) {
      console.error('❌ Error saving verification:', error);
      
      // Fallback to localStorage for development
      const verificationRecord: VerificationRecord = {
        id: `verification-${Date.now()}`,
        userId: data.userId,
        status: data.isDraft ? VerificationStatus.DRAFT : VerificationStatus.PENDING_REVIEW,
        purpose: data.purpose,
        data,
        completionPercentage: progress.completionPercentage,
        missingFields: progress.missingFields,
        submittedAt: data.isDraft ? undefined : new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(`verification-${data.userId}`, JSON.stringify(verificationRecord));
      
      return {
        success: true,
        verification: verificationRecord
      };
    }
  },

  /**
   * 📋 Get user's verification record
   */
  async getUserVerification(userId: string): Promise<{
    success: boolean;
    verification?: VerificationRecord;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/driver-verification/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 404) {
        return { success: true, verification: undefined };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        verification: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching user verification:', error);
      
      // Fallback to localStorage
      const stored = localStorage.getItem(`verification-${userId}`);
      if (stored) {
        const verification = JSON.parse(stored);
        
        // Check if there's rejection info for this user
        const rejectionInfo = verificationPersistence.getRejectedFieldInfo(userId);
        if (rejectionInfo && verification.status === VerificationStatus.REJECTED) {
          verification.rejectedFields = rejectionInfo.rejectedFields;
          verification.rejectionDetails = {
            reason: rejectionInfo.rejectionReason,
            rejectedAt: rejectionInfo.rejectedAt,
            fields: rejectionInfo.rejectedFields
          };
        }
        
        return {
          success: true,
          verification
        };
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch verification'
      };
    }
  },

  /**
   * 🚀 Submit verification for review
   */
  async submitVerification(userId: string): Promise<{
    success: boolean;
    verification?: VerificationRecord;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/driver-verification/${userId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        verification: result.data
      };
    } catch (error) {
      console.error('❌ Error submitting verification:', error);
      
      // Fallback simulation
      const stored = localStorage.getItem(`verification-${userId}`);
      if (stored) {
        const verification = JSON.parse(stored);
        verification.status = VerificationStatus.PENDING_REVIEW;
        verification.submittedAt = new Date().toISOString();
        localStorage.setItem(`verification-${userId}`, JSON.stringify(verification));
        
        return {
          success: true,
          verification
        };
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit verification'
      };
    }
  },

  /**
   * 📤 Upload document
   */
  async uploadDocument(file: File, documentType: string, userId: string): Promise<{
    success: boolean;
    fileUrl?: string;
    error?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      formData.append('userId', userId);

      const response = await fetch(`${getApiBaseUrl()}/api/v1/driver-verification/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        fileUrl: result.data.fileUrl
      };
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      
      // Simulate file upload for development
      const simulatedUrl = `https://storage.example.com/${userId}/${documentType}/${file.name}`;
      
      return {
        success: true,
        fileUrl: simulatedUrl
      };
    }
  },

  /**
   * 👨‍💼 Admin: Get all verification records with filtering
   */
  async getVerifications(params: {
    status?: VerificationStatus;
    purpose?: string;
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<{
    success: boolean;
    data?: VerificationListResponse;
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.purpose) queryParams.append('purpose', params.purpose);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/driver-verifications?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching verifications:', error);
      
      // Mock data for development
      const mockVerifications: VerificationRecord[] = [
        {
          id: 'ver-001',
          userId: 'user-001',
          status: VerificationStatus.PENDING_REVIEW,
          purpose: 'driver_application',
          data: {} as VerificationData,
          completionPercentage: 100,
          missingFields: [],
          submittedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'ver-002',
          userId: 'user-002',
          status: VerificationStatus.DRAFT,
          purpose: 'customer_verification',
          data: {} as VerificationData,
          completionPercentage: 75,
          missingFields: ['Banking Information'],
          createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
        }
      ];

      return {
        success: true,
        data: {
          verifications: mockVerifications,
          total: mockVerifications.length,
          page: params.page || 1,
          limit: params.limit || 10,
          hasMore: false
        }
      };
    }
  },

  /**
   * ✅ Admin: Approve verification (using admin endpoint)
   */
  async approveVerification(verificationId: string, adminNotes?: string): Promise<{
    success: boolean;
    verification?: VerificationRecord;
    error?: string;
  }> {
    try {
      log.info('Starting approval process', { verificationId });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const apiUrl = `${getApiBaseUrl()}/api/admin/verification-requests/${verificationId}/approve`;
      log.debug('Calling API', { apiUrl });

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          notes: adminNotes || undefined // Optional field - adminUserId comes from JWT
        })
      });

      log.debug('API Response received', { status: response.status });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        if (response.status === 401) {
          errorMessage = 'Authentication failed. Your session may have expired. Please log in again.';
        }

        try {
          const errorData = await response.json();
          log.error('Error response from API', { status: response.status, errorData });
          if (errorData.error || errorData.message) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch (e) {
          // Response is not JSON, use status message
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      log.info('Approval successful', { verificationId, result });

      return {
        success: true,
        verification: result.data
      };
    } catch (error) {
      log.error('Error approving verification', { verificationId, error });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve verification'
      };
    }
  },

  /**
   * ❌ Admin: Reject verification (using admin endpoint)
   */
  async rejectVerification(verificationId: string, rejectionReason: string, adminNotes?: string, rejectedFields?: string[]): Promise<{
    success: boolean;
    verification?: VerificationRecord;
    error?: string;
  }> {
    try {

      // Prepare request body according to API spec
      const requestBody: any = {
        rejectionReason // REQUIRED
      };

      // Add optional fields only if provided
      if (rejectedFields && rejectedFields.length > 0) {
        requestBody.rejectedFields = rejectedFields;
      }
      if (adminNotes) {
        requestBody.notes = adminNotes; // Note: it's "notes", not "adminNotes"
      }

      const response = await fetch(`${getApiBaseUrl()}/api/admin/verification-requests/${verificationId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Optionally save rejection info locally for immediate UI feedback
      if (result.data && result.data.userId) {
        verificationPersistence.saveRejectedFieldInfo({
          verificationId,
          userId: result.data.userId,
          rejectedAt: new Date().toISOString(),
          rejectedFields: rejectedFields || [],
          rejectionReason,
          preservedData: result.data.preservedData || {}
        });
      }
      
      return {
        success: result.success || true,
        verification: result.data
      };
    } catch (error) {
      console.error('❌ Error rejecting verification:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject verification'
      };
    }
  },

  /**
   * 📊 Get verification statistics
   */
  async getVerificationStats(): Promise<{
    success: boolean;
    stats?: {
      total: number;
      pending: number;
      approved: number;
      rejected: number;
      draft: number;
      averageReviewTime: number; // in hours
    };
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/driver-verifications/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        stats: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching verification stats:', error);
      
      // Mock stats for development
      return {
        success: true,
        stats: {
          total: 156,
          pending: 23,
          approved: 98,
          rejected: 12,
          draft: 23,
          averageReviewTime: 18.5
        }
      };
    }
  },

  // ========================================
  // 🆕 NEW ADMIN VERIFICATION ENDPOINTS
  // ========================================

  /**
   * 📋 Admin: Get basic user verifications (ACTUAL API ENDPOINT)
   * Uses: GET /api/admin/verification-requests
   */
  async getAdminVerificationRequests(params: {
    status?: 'pending_review' | 'verified' | 'rejected' | 'all';
  } = {}): Promise<{
    success: boolean;
    data?: AdminVerificationListResponse;
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.status && params.status !== 'all') {
        queryParams.append('status', params.status);
      }

      const response = await fetch(`${getApiBaseUrl()}/api/admin/verification-requests?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Transform data to match our interface expectations
      const transformedResponse: AdminVerificationListResponse = {
        success: result.success,
        data: result.data || [],
        total: result.total || 0
      };
      
      return {
        success: true,
        data: transformedResponse
      };
    } catch (error) {
      console.error('❌ Error fetching admin verification requests:', error);
      
      // Mock data for development/fallback - matching actual API structure
      const mockRequests: AdminVerificationRequest[] = [
        {
          id: 'ver-001',
          verificationId: 'VER_1757945832073_s0b4an',
          userId: 'user-001',
          status: 'pending_review',
          documentType: 'passport',
          countryCode: 'NG',
          documentImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#2196F3"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">PASSPORT DOCUMENT</text>
            </svg>
          `),
          selfieImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#4CAF50"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">SELFIE PHOTO</text>
            </svg>
          `),
          userInfo: {
            firstName: 'John',
            lastName: 'Doe',
            email: 'john@example.com',
            phone: '+2348012345678'
          },
          uploadedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'ver-002',
          verificationId: 'VER_1757945832074_t1c5bo',
          userId: 'user-002',
          status: 'pending_review',
          documentType: 'national_id',
          countryCode: 'NG',
          documentImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#FF9800"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">NATIONAL ID</text>
            </svg>
          `),
          selfieImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#9C27B0"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">SELFIE PHOTO</text>
            </svg>
          `),
          userInfo: {
            firstName: 'Jane',
            lastName: 'Smith',
            email: 'jane@example.com',
            phone: '+2348087654321'
          },
          uploadedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
        }
      ];

      return {
        success: true,
        data: {
          success: true,
          data: mockRequests,
          total: mockRequests.length
        }
      };
    }
  },

  /**
   * 🚗 Admin: Get driver verifications (DRIVER APPLICATIONS)
   * Uses: GET /api/v1/admin/driver-verifications
   */
  async getDriverVerifications(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{
    success: boolean;
    data?: DriverVerificationListResponse;
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/driver-verifications?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('❌ Error fetching driver verifications:', error);
      
      // Mock driver verification data
      const mockDriverVerifications: DriverVerificationRequest[] = [
        {
          id: 'drv-001',
          userId: 'driver-001',
          userName: 'Mike Johnson',
          userEmail: 'mike@example.com',
          submittedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          status: 'pending_review',
          completionPercentage: 85,
          documentsCount: 4,
          hasRejectionReason: false
        },
        {
          id: 'drv-002',
          userId: 'driver-002',
          userName: 'Sarah Wilson',
          userEmail: 'sarah@example.com',
          submittedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          status: 'under_review',
          completionPercentage: 100,
          documentsCount: 5,
          hasRejectionReason: false
        }
      ];

      return {
        success: true,
        data: {
          success: true,
          data: {
            verifications: mockDriverVerifications,
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalCount: mockDriverVerifications.length,
              limit: 20,
              hasNextPage: false,
              hasPrevPage: false
            },
            statistics: {
              total: mockDriverVerifications.length,
              byStatus: {
                pending_review: 1,
                under_review: 1,
                approved: 0,
                rejected: 0,
                cancelled: 0
              }
            }
          }
        }
      };
    }
  },

  /**
   * ✅❌ Admin: Approve or reject verification request
   * Uses backend endpoints:
   *   - POST /api/admin/verification-requests/:id/approve
   *   - POST /api/admin/verification-requests/:id/reject
   */
  async updateVerificationRequestStatus(
    verificationId: string,
    action: 'approve' | 'reject',
    adminUserId: string,
    notes?: string,
    rejectedFields?: string[]
  ): Promise<{
    success: boolean;
    data?: AdminVerificationRequest;
    error?: string;
  }> {
    try {
      log.info(`${action === 'approve' ? 'Approving' : 'Rejecting'} verification`, { verificationId });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Use the correct endpoint based on action
      const endpoint = action === 'approve'
        ? `${getApiBaseUrl()}/api/admin/verification-requests/${verificationId}/approve`
        : `${getApiBaseUrl()}/api/admin/verification-requests/${verificationId}/reject`;

      log.debug('Calling API', { endpoint, action });

      // Prepare request body based on action
      const requestBody: any = {};

      if (notes) {
        requestBody.notes = notes;
      }

      // Only add rejectionReason and rejectedFields for reject action
      if (action === 'reject') {
        requestBody.rejectionReason = notes || 'Rejected by admin';
        if (rejectedFields && rejectedFields.length > 0) {
          requestBody.rejectedFields = rejectedFields;
        }
      }

      log.debug('Request body', { requestBody });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      log.debug('API Response received', { status: response.status });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        if (response.status === 401) {
          errorMessage = 'Authentication failed. Your session may have expired. Please log in again.';
        }

        try {
          const errorData = await response.json();
          log.error('Error response from API', { status: response.status, errorData });
          if (errorData.error || errorData.message) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch (e) {
          // Response is not JSON, use status message
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      log.info(`${action} successful`, { verificationId, result });

      // Save rejection info locally if action is reject
      if (action === 'reject' && result.data && result.data.userId) {
        verificationPersistence.saveRejectedFieldInfo({
          verificationId,
          userId: result.data.userId,
          rejectedAt: new Date().toISOString(),
          rejectedFields: rejectedFields || [],
          rejectionReason: notes || 'Rejected by admin',
          preservedData: result.data.preservedData || {}
        });
      }

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      log.error(`Error ${action}ing verification`, { verificationId, error });
      return {
        success: false,
        error: error instanceof Error ? error.message : `Failed to ${action} verification`
      };
    }
  },

  /**
   * 🖼️ Get verification image URL (NEW ENDPOINT)
   * Uses the new backend endpoint: /api/images/verification/[...path]
   */
  getVerificationImageUrl(imagePath: string): string {
    // If it's a data URI, return as-is
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // If it's already a full URL (like placeholder.com), return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // For backend API paths, add API_BASE
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${getApiBaseUrl()}/${cleanPath}`;
  },

  /**
   * 📥 Download verification document
   * Helper method for downloading verification documents
   */
  async downloadVerificationDocument(imagePath: string): Promise<{
    success: boolean;
    blob?: Blob;
    error?: string;
  }> {
    try {
      const token = localStorage.getItem('token');
      const imageUrl = this.getVerificationImageUrl(imagePath);

      console.log('🔐 Downloading verification document:', imageUrl);
      console.log('Auth token present:', !!token);

      const response = await fetch(imageUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          const isDev = getApiBaseUrl().includes('localhost');
          if (isDev) {
            console.error('❌ 401 in dev - run clearDevAuth() and login again');
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return {
        success: true,
        blob
      };
    } catch (error) {
      console.error('❌ Error downloading verification document:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to download document'
      };
    }
  },

  /**
   * 🔒 Fetch verification image with PII token (SECURE)
   * Requires password re-authentication and uses X-PII-Verification-Token header
   * Returns blob URL for display in <img> tags
   */
  async fetchVerificationImageWithPIIToken(
    imagePath: string,
    piiToken: string
  ): Promise<{
    success: boolean;
    blobUrl?: string;
    error?: string;
  }> {
    try {
      const token = localStorage.getItem('token');
      const imageUrl = this.getVerificationImageUrl(imagePath);

      console.group('🔐 Fetching Verification Image with PII Token');
      console.log('Image URL:', imageUrl);
      console.log('Auth token present:', !!token);
      console.log('PII token present:', !!piiToken);
      console.groupEnd();

      const response = await fetch(imageUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-PII-Verification-Token': piiToken,
        },
      });

      console.log('Image fetch response status:', response.status);

      if (response.status === 401 || response.status === 403) {
        const isDev = getApiBaseUrl().includes('localhost');
        const errorMsg = isDev
          ? 'Dev Mode: Auth token invalid for localhost. Run clearDevAuth() in console and re-login with dev credentials.'
          : 'PII token expired or invalid';

        console.error('❌ Image fetch failed:', errorMsg);

        if (isDev) {
          console.log('%c💡 SOLUTION:', 'color: #00ff00; font-weight: bold; font-size: 14px');
          console.log('1. Open browser console');
          console.log('2. Run: clearDevAuth()');
          console.log('3. Login again with dev credentials');
        }

        // PII token expired or invalid
        return {
          success: false,
          error: 'PII access token expired or invalid. Please re-verify your password.',
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);


      return {
        success: true,
        blobUrl,
      };
    } catch (error) {
      console.error('❌ Error fetching verification image with PII token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch image',
      };
    }
  },

  // ========================================
  // 🖼️ PHOTO REVIEW ENDPOINTS
  // ========================================

  /**
   * 📸 Get user verification photos
   * Uses: GET /api/v1/admin/verification/photos/[userId]
   */
  async getUserVerificationPhotos(userId: string): Promise<{
    success: boolean;
    data?: {
      userId: string;
      userName: string;
      documentPhotoUrl: string;
      selfiePhotoUrl: string;
      identityPhotoUrl: string;
      status: 'pending_review' | 'approved' | 'rejected';
      livenessCheck?: {
        passed: boolean;
        score: number;
      };
      uploadedAt: string;
    };
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/verification/photos/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching user verification photos:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch verification photos'
      };
    }
  },

  /**
   * ✅❌ Review user verification photos
   * Uses: PUT /api/v1/admin/verification/photos/[userId]/review
   */
  async reviewVerificationPhotos(
    userId: string,
    action: 'approve' | 'reject',
    notes: string,
    reviewedBy: string
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/verification/photos/${userId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          notes,
          reviewedBy
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || `Photos ${action}ed successfully`
      };
    } catch (error) {
      console.error('❌ Error reviewing verification photos:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to review photos'
      };
    }
  },

  /**
   * 🌍 Get regional compliance statistics
   * Uses: GET /api/v1/admin/verification/regional-compliance
   */
  async getRegionalCompliance(region?: string): Promise<{
    success: boolean;
    data?: {
      regions: Array<{
        region: string;
        countryCode: string;
        totalUsers: number;
        verifiedUsers: number;
        pendingVerifications: number;
        rejectedVerifications: number;
        complianceRate: number;
        requirements: Array<{
          name: string;
          requirementType: string;
          status: 'Compliant' | 'Partial' | 'Non-Compliant';
          details: string;
          compliantCount: number;
          totalCount: number;
          compliancePercentage: number;
        }>;
        lastUpdated: string;
      }>;
      summary: {
        totalRegions: number;
        totalUsers: number;
        totalVerified: number;
        overallComplianceRate: number;
      };
    };
    error?: string;
  }> {
    try {
      const params = region ? `?region=${encodeURIComponent(region)}` : '';
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/verification/regional-compliance${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching regional compliance:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch regional compliance'
      };
    }
  },

  /**
   * 🚩 Flag a verification issue
   * Uses: POST /api/v1/admin/verification/:verificationId/flag
   */
  async flagVerification(
    verificationId: string,
    reason: string,
    flagType: string = 'other'
  ): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/verification/${verificationId}/flag`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason,
          flagType,
          notes: reason
        })
      });

      // Try to parse error response
      const responseText = await response.text();

      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { message: responseText || `HTTP error! status: ${response.status}` };
        }

        const errorMessage = errorData.error?.message || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const result = JSON.parse(responseText);
      return {
        success: true,
        message: result.message || 'Verification flagged successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error flagging verification:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to flag verification'
      };
    }
  },

  /**
   * 🗑️ Delete a verification record
   * Uses: DELETE /api/v1/admin/verification/:verificationId
   */
  async deleteVerification(
    verificationId: string,
    reason: string = 'Admin deletion'
  ): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const adminUserId = localStorage.getItem('userId') || 'admin';


      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/verification/${verificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason,
          deletedBy: adminUserId
        })
      });

      // Try to parse error response
      const responseText = await response.text();

      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { message: responseText || `HTTP error! status: ${response.status}` };
        }

        const errorMessage = errorData.error?.message || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const result = JSON.parse(responseText);
      return {
        success: true,
        message: result.message || 'Verification deleted successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error deleting verification:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete verification'
      };
    }
  },

  /**
   * 🚫 Blacklist a passport number
   * Uses: POST /api/v1/admin/verification/blacklist/passport
   */
  async blacklistPassport(
    passportNumber: string,
    reason: string,
    userId: string,
    adminId: string
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/verification/blacklist/passport`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          passportNumber,
          reason,
          userId,
          blacklistedBy: adminId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || 'Passport blacklisted successfully'
      };
    } catch (error) {
      console.error('❌ Error blacklisting passport:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to blacklist passport'
      };
    }
  },

  /**
   * 📜 Get all verification records for a specific user
   * Filters verification requests by userId client-side
   * Returns complete verification history including all document types
   */
  async getUserVerificationHistory(userId: string): Promise<{
    success: boolean;
    data?: AdminVerificationRequest[];
    error?: string;
  }> {
    try {
      // Fetch all verification requests
      const result = await this.getAdminVerificationRequests({ status: 'all' });

      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch verification requests'
        };
      }

      // Filter by userId client-side
      const userVerifications = result.data.data.filter(v => v.userId === userId);

      // Sort by upload date (newest first)
      userVerifications.sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );

      return {
        success: true,
        data: userVerifications
      };
    } catch (error) {
      console.error('❌ Error fetching user verification history:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch user verification history'
      };
    }
  }
};